/**
 * Chiamate all'API. Il cookie di sessione viaggia da solo (`credentials: 'include'`),
 * quindi non c'e' nessun token da conservare nel browser: niente localStorage, niente
 * possibilita' che un frammento di codice altrui se lo porti via.
 */
const BASE = '/api'

export class ErroreApi extends Error {
  stato: number
  codice?: string
  dettagli?: { campo: string; messaggio: string }[]
  corpo?: unknown
  constructor(messaggio: string, stato: number, corpo?: unknown) {
    super(messaggio)
    this.stato = stato
    this.corpo = corpo
    const c = corpo as { codice?: string; dettagli?: { campo: string; messaggio: string }[] } | undefined
    this.codice = c?.codice
    this.dettagli = c?.dettagli
  }
}

type Opzioni = {
  metodo?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  corpo?: unknown
  motivo?: string          // motivazione dell'accesso, per le operazioni che la richiedono
  segnale?: AbortSignal
  /**
   * Richiesta di un temporizzatore, non di una persona: sulle rotte che lo prevedono non rinnova la
   * sessione (ROTTE_ANCHE_DI_POLLING in apps/api/src/plugin/sessione.ts).
   */
  automatica?: boolean
  /**
   * La richiesta sopravvive alla pagina che si nasconde o se ne va (fetch con `keepalive`): le ultime modifiche
   * di una bozza (3/11/2026). Il browser ne limita il corpo a 64 KB.
   */
  keepalive?: boolean
}

/**
 * Chi gestisce la sessione scaduta a meta' lavoro (521, servizi/sessione.tsx). Nell'app Android riapre la
 * sessione dal telefono ricordato e la richiesta si ripete una volta; nella web app dell'iPhone mostra lo
 * sblocco con Face ID e la richiesta fallisce come prima.
 */
let gestoreSessioneScaduta: (() => Promise<boolean>) | null = null
export function gestisciSessioneScaduta(f: (() => Promise<boolean>) | null) { gestoreSessioneScaduta = f }

/**
 * Impersonazione in sola lettura (524): chi impersona non scrive niente, e l'API lo ferma
 * (plugin/sessione.ts). L'interfaccia non manda nemmeno la richiesta: un comando che scrive risponde
 * subito con lo stesso errore, e le chiamate automatiche (telefono ricordato, notifiche push, messaggio
 * letto) non partono. Lo imposta servizi/sessione.tsx con il nome dell'utente impersonato.
 */
let solaLettura: string | null = null
export function impostaSolaLettura(nome: string | null) { solaLettura = nome }
const CONSENTITE_IN_SOLA_LETTURA = [
  /^\/auth\/impersonazione\/fine$/, /^\/auth\/esci$/, /^\/posta\/(caselle\/[^/]+|tutte)\/(thread|messaggi)\/cerca$/,
]

export async function chiama<T>(percorso: string, opzioni: Opzioni = {}, ripetuta = false): Promise<T> {
  if (solaLettura && (opzioni.metodo ?? 'GET') !== 'GET'
      && !CONSENTITE_IN_SOLA_LETTURA.some((r) => r.test(percorso.split('?')[0]))) {
    throw new ErroreApi(`Stai vedendo il gestionale come ${solaLettura}, in sola lettura: non si modifica niente. `
      + '«Torna al tuo profilo» per lavorare con il tuo.', 403, { codice: 'IMPERSONAZIONE_SOLA_LETTURA' })
  }
  const intestazioni: Record<string, string> = {}
  if (opzioni.corpo !== undefined) intestazioni['content-type'] = 'application/json'
  if (opzioni.motivo) intestazioni['x-motivo-accesso'] = opzioni.motivo
  if (opzioni.automatica) intestazioni['x-richiesta-automatica'] = '1'

  const risposta = await fetch(BASE + percorso, {
    method: opzioni.metodo ?? 'GET',
    headers: intestazioni,
    body: opzioni.corpo === undefined ? undefined : JSON.stringify(opzioni.corpo),
    credentials: 'include',
    signal: opzioni.segnale,
    ...(opzioni.keepalive ? { keepalive: true } : {}),
  })

  const testo = await risposta.text()
  const dati = testo ? (() => { try { return JSON.parse(testo) } catch { return testo } })() : null

  if (risposta.status === 401 && !ripetuta && gestoreSessioneScaduta && !percorso.startsWith('/auth/')
      && (dati as { codice?: string } | null)?.codice === 'NON_AUTENTICATO') {
    if (await gestoreSessioneScaduta()) return chiama<T>(percorso, opzioni, true)
  }

  if (!risposta.ok) {
    // L'API risponde 404 con lo stesso testo sia quando una cosa non esiste sia quando sta fuori
    // dagli incarichi di chi la chiede (errori.ts): qui diventa una frase che vale per tutti e due.
    const errore = (dati as { errore?: string })?.errore
    const messaggio = risposta.status === 404
      ? 'Non c’è, oppure non rientra fra quello che puoi vedere con i tuoi incarichi.'
      : errore ?? `Il server non ha risposto come doveva (errore ${risposta.status}). Riprova fra poco.`
    throw new ErroreApi(messaggio, risposta.status, dati)
  }
  return dati as T
}

export const api = {
  get: <T>(p: string, o?: Omit<Opzioni, 'metodo' | 'corpo'>) => chiama<T>(p, { ...o, metodo: 'GET' }),
  post: <T>(p: string, corpo?: unknown, o?: Omit<Opzioni, 'metodo' | 'corpo'>) =>
    chiama<T>(p, { ...o, metodo: 'POST', corpo }),
  patch: <T>(p: string, corpo?: unknown, o?: Omit<Opzioni, 'metodo' | 'corpo'>) =>
    chiama<T>(p, { ...o, metodo: 'PATCH', corpo }),
  put: <T>(p: string, corpo?: unknown, o?: Omit<Opzioni, 'metodo' | 'corpo'>) =>
    chiama<T>(p, { ...o, metodo: 'PUT', corpo }),
  elimina: <T>(p: string, corpo?: unknown, o?: Omit<Opzioni, 'metodo' | 'corpo'>) =>
    chiama<T>(p, { ...o, metodo: 'DELETE', corpo }),
}

// --- forme dei dati che l'interfaccia usa -------------------------------------------------

export type Incarico = {
  ruolo: string
  /** Nome del ruolo, per mostrarlo; il codice resta in `ruolo`. */
  ruoloNome?: string
  /**
   * `nome`: sede, area, progetto, ente o tipologia per esteso; nullo per tutta CIDIS. `sedi`, per
   * un incarico di sede: la sede e i suoi sportelli (463), su cui l'incarico vale.
   */
  ambito: { tipo: string; id: string | null; nome?: string | null; sedi?: string[] }
  soloPropri: boolean
  scadenza: string | null
  permessi: string[]
}

export type StatoSecondoFattore = {
  attivo: boolean
  /** Da quando i tuoi incarichi lo esigono, AAAA-MM-GG; null se nessuno lo esige. */
  obbligatorioDal: string | null
  /** Obbligo in vigore e fattore non attivo: il gestionale apre solo la configurazione. */
  daAttivareOra: boolean
  /** Ultimo giorno del periodo di grazia, AAAA-MM-GG, finche' dura e il fattore non e' attivo. */
  daAttivareEntro: string | null
  /** Facoltativo e non attivo (496): una riga in cima alla pagina lo propone. Mai agli utenti di prova. */
  consigliato: boolean
  /** Codici di recupero non ancora usati; null se il fattore non e' attivo o non e' verificato. */
  codiciRimasti: number | null
}

export type Io = {
  id: string; email: string; nome: string; cognome: string; tipo: string
  mfaAttiva: boolean; mfaVerificata: boolean
  /** Secondo fattore (493): stato e obbligo, per la pagina «Secondo fattore» e l'avviso della grazia. */
  secondoFattore: StatoSecondoFattore
  /**
   * Caselle di posta da associare a persone o progetti (504): il numero per chi gestisce le utenze,
   * null per gli altri. Alimenta la riga in cima alla pagina.
   */
  caselleDaAssociare?: number | null
  /**
   * Email personale di recupero (520): se e' verificata, e se la riga in cima alla pagina la chiede
   * (dipendenti senza un'email verificata).
   */
  emailRecupero?: { verificata: boolean; daImpostare: boolean }
  /** Password provvisoria: l'applicazione non si apre finche' non la cambia. */
  deveCambiarePassword: boolean
  incarichi: Incarico[]
  permessi: string[]
  /** Sedi CIDIS dell'operatore, la principale per prima (D-25). Anagrafica, non perimetro. */
  sedi?: { id: string; codice: string; nome: string; principale: boolean }[]
  /**
   * Indirizzi pubblici (479): il gestionale, su cui si costruiscono i riferimenti alle schede, e il
   * messenger, null finche' non e' configurato.
   */
  indirizzi?: { gestionale: string; messenger: string | null }
  /**
   * Il telefono da cui si entra, se e' ricordato (521): `app` (Android) o `schermata_home` (web app
   * dell'iPhone, che vale solo con la passkey). Dal 2/11/2026 anche il computer ricordato (531, `computer`,
   * con la passkey di Windows Hello o Touch ID). Null altrove.
   */
  telefono?: { tipo: 'app' | 'schermata_home' | 'computer'; passkey: boolean } | null
  /**
   * Palestra (490): `accesa` per questa sessione, `disponibile` se c'e' una sede di formazione e
   * l'utente ha incarichi veri da provare. Con la palestra accesa incarichi, permessi e sedi qui
   * sopra sono gia' quelli della sede di formazione.
   */
  palestra?: { accesa: boolean; disponibile: boolean }
  /**
   * Impersonazione in sola lettura (524): chi sta vedendo il gestionale come questo utente, e da quando.
   * Tutto il resto qui sopra e' dell'utente impersonato. Null fuori dall'impersonazione.
   */
  impersonazione?: { da: { nome: string; cognome: string }; iniziataIl: string | null } | null
  /** «Vedi come questo utente» nella pagina di un utente: solo per chi puo' impersonare (524). */
  puoImpersonare?: boolean
}

/**
 * Riga di GET /persone. Sulle righe mascherate `nome` e `cognome` sono le iniziali («A***»), la
 * nascita e' solo `anno_nascita`, e cittadinanza e comune mancano (D-02).
 */
export type VocePersona = {
  id: string
  numero_scheda: number
  etichetta: string
  nome: string
  cognome: string
  data_nascita?: string | null
  anno_nascita?: string | null
  stato: string
  sede_riferimento: string | null
  ha_area_riservata: boolean
  da_verificare: boolean
  /** Scheda con dati anonimizzati (481): nome e cognome sono iniziali. */
  dati_anonimizzati?: boolean
  cittadinanza?: string | null
  /** Nome del paese di cittadinanza (ref.paese). */
  cittadinanza_nome?: string | null
  /** Codice ISTAT del comune di residenza, altrimenti di domicilio. */
  comune?: string | null
  comune_tipo?: 'residenza' | 'domicilio' | null
  comune_nome?: string | null
  comune_provincia?: string | null
  aggiornata_il: string
  mascherata: boolean
}

export type Tassonomia = {
  aree: { codice: string; numero: number; nome: string; colore: string | null; attiva: boolean }[]
  tipologie: { codice: string; area_codice: string; nome: string; gestione: string
    classe_dato: string; attiva: boolean; storica: boolean
    /** La registrazione chiede le ore di mediazione, multipli di mezz'ora da 0,5 a 10. */
    con_ore_mediazione?: boolean }[]
  sottotipologie: { codice: string; tipologia_codice: string; nome: string; attiva: boolean; storica: boolean }[]
  temi: { codice: string; nome: string; classe_dato: string; attivo: boolean }[]
}

export type Paese = { codice_iso3: string; nome: string; ue: boolean }
export type Campo = {
  codice: string
  etichetta: string
  tipo: 'testo' | 'testo_lungo' | 'numero' | 'data' | 'booleano' | 'scelta' | 'scelta_multipla' | 'catalogo'
  obbligatorio?: boolean
  catalogo?: string
  opzioni?: { valore: string; etichetta: string }[]
  min?: number
  max?: number
  classe_dato?: 'personale' | 'particolare' | 'giudiziario' | 'riservato'
  aiuto?: string
}
export type SchemaModulo = { campi: Campo[] }

export type VoceCatalogo = { codice: string; nome: string; classe_dato?: string; ordinamento?: number }
export type Cataloghi = Record<string, VoceCatalogo[]>
